import type { DeleteCommentResult, Thread } from '@atlas/api-contract';

import { useElementSize } from '@/lib/hooks/use-element-size';
import type { PendingPin } from '@/features/viewer/viewer-store';
import { cn } from '@/lib/utils';

import { NewThreadComposer } from './new-thread-composer';
import {
  PIN_HIT_TARGET_PX,
  positionPinHitTarget,
  positionPinPopover,
} from './pin-popover-position';
import {
  selectPinPosition,
  usePinPositionsStore,
} from './pin-positions-store';
import { ThreadPopover } from './thread-popover';

export interface PinOverlayProps {
  readonly versionId: string;
  /** The threads whose pins the agent has been told to draw. */
  readonly pinThreads: readonly Thread[];
  readonly selectedThread: Thread | null;
  readonly pendingPin: PendingPin | null;
  readonly onSelect: (threadId: string) => void;
  readonly onClose: () => void;
  readonly onHover: (threadId: string | null) => void;
  readonly onCancelPending: () => void;
  readonly onCreated: (thread: Thread) => void;
  readonly onCommentDeleted: (result: DeleteCommentResult) => void;
}

/**
 * The parent's half of the pin surface.
 *
 * ============================================================================
 * WHAT IS DRAWN WHERE, AND WHY IT IS SPLIT THAT WAY
 * ============================================================================
 * The *markers* are drawn inside the prototype by the agent: only code running
 * in that document can find the anchored element, follow it as the page
 * reflows, and stay pinned to it through the prototype's own scrolling
 * containers. The *popover* is drawn out here, because it is Atlas's UI — it
 * needs the theme, the shadcn primitives, React Query and the identity gate,
 * none of which exist inside a sandboxed third-party document, and injecting
 * them would mean shipping the app twice.
 *
 * That split leaves one gap: the parent gets no mouse events for a marker
 * inside the iframe, and the protocol has an `atlas:pin-clicked` but no
 * `atlas:pin-hovered`. So a small transparent hit target is laid over each
 * marker out here — the size of the marker and no larger, so clicks meant for
 * the prototype still reach it — which is what makes "hovering a pin highlights
 * its panel row" (spec R6.3) possible without changing the protocol. A click on
 * one selects the thread directly; `atlas:pin-clicked` still arrives for the
 * wider part of a marker carrying a reply count, and does the same thing.
 *
 * ============================================================================
 * HOW THE POPOVER STAYS GLUED
 * ============================================================================
 * This element is `absolute inset-0` inside the frame's positioning wrapper, so
 * its coordinate space *is* the iframe's viewport (offset by the frame's 1px
 * border). Everything that moves the frame — the canvas scrolling, the 200 ms
 * width-preset transition, a window resize — moves this layer with it as part
 * of the same layout, with no listener and no measurement.
 *
 * The only motion left to track is the pin's inside the prototype, and the
 * agent streams that on `atlas:pin-positions`, already throttled to one message
 * per animation frame. Each message is a React state write and a re-render of
 * this layer and nothing else, which is why the store behind it is separate
 * from `viewer-store`. There is deliberately no `requestAnimationFrame` loop
 * here: a second clock would either idle or interpolate towards a position the
 * agent has already superseded, and the visible result of that is jitter.
 */
export const PinOverlay = ({
  versionId,
  pinThreads,
  selectedThread,
  pendingPin,
  onSelect,
  onClose,
  onHover,
  onCancelPending,
  onCreated,
  onCommentDeleted,
}: PinOverlayProps) => {
  const { ref: overlayRef, size: frame } = useElementSize();
  const { ref: popoverRef, size: popover } = useElementSize();
  const { ref: composerRef, size: composer } = useElementSize();

  const selectedPosition = usePinPositionsStore(
    selectPinPosition(selectedThread?.id ?? null),
  );

  const showPopover = selectedThread !== null && selectedPosition !== null;

  const popoverPosition =
    showPopover && frame.width > 0
      ? positionPinPopover(selectedPosition, popover, frame)
      : null;

  const composerPosition =
    pendingPin === null || frame.width === 0
      ? null
      : positionPinPopover(
          { x: pendingPin.viewportX, y: pendingPin.viewportY },
          composer,
          frame,
        );

  return (
    <div
      ref={overlayRef}
      /*
       * `pointer-events-none` on the layer, re-enabled per child: everything
       * this does not explicitly claim has to reach the prototype underneath,
       * or comment mode would be the only mode the viewer had.
       */
      className="pointer-events-none absolute inset-0 z-10"
    >
      <PinHitLayer
        pinThreads={pinThreads}
        selectedThreadId={selectedThread?.id ?? null}
        onSelect={onSelect}
        onHover={onHover}
      />

      {selectedThread === null ? null : (
        <div
          ref={popoverRef}
          className={cn(
            'pointer-events-auto absolute w-80',
            /*
             * Positioned even while hidden, and hidden by opacity rather than
             * unmounted: a popover that unmounted every time its pin scrolled
             * off would lose the reply being typed into it, and would have to
             * be re-measured — and therefore visibly jump — on the way back.
             */
            popoverPosition === null ||
              popover.height === 0 ||
              !(selectedPosition?.visible ?? false)
              ? 'pointer-events-none opacity-0'
              : 'opacity-100',
          )}
          style={{
            left: `${String(popoverPosition?.left ?? 0)}px`,
            top: `${String(popoverPosition?.top ?? 0)}px`,
          }}
        >
          <ThreadPopover
            thread={selectedThread}
            versionId={versionId}
            approximate={selectedPosition?.approximate ?? false}
            onClose={onClose}
            onCommentDeleted={onCommentDeleted}
          />
        </div>
      )}

      {pendingPin === null ? null : (
        <div
          ref={composerRef}
          className={cn(
            'pointer-events-auto absolute w-80',
            composerPosition === null || composer.height === 0
              ? 'opacity-0'
              : 'opacity-100',
          )}
          style={{
            left: `${String(composerPosition?.left ?? 0)}px`,
            top: `${String(composerPosition?.top ?? 0)}px`,
          }}
        >
          {/*
            The candidate has no marker of its own to follow: the agent only
            streams positions for pins it has drawn, and a pin that does not
            exist yet is not one of them. So the composer sits where the click
            landed. Scrolling the prototype underneath it would leave it behind,
            which is why the store discards a candidate the moment the screen
            changes rather than letting it drift.
          */}
          <NewThreadComposer
            pin={pendingPin}
            versionId={versionId}
            onCancel={onCancelPending}
            onCreated={onCreated}
          />
        </div>
      )}
    </div>
  );
};

interface PinHitLayerProps {
  readonly pinThreads: readonly Thread[];
  readonly selectedThreadId: string | null;
  readonly onSelect: (threadId: string) => void;
  readonly onHover: (threadId: string | null) => void;
}

/**
 * Transparent hover and click targets over the agent's markers.
 *
 * Subscribed to the whole position map, unlike the popover which watches one
 * entry, because every target moves on every scroll. It is the only component
 * in the app that re-renders at animation-frame rate, and it renders nothing
 * but a handful of empty buttons.
 */
const PinHitLayer = ({
  pinThreads,
  selectedThreadId,
  onSelect,
  onHover,
}: PinHitLayerProps) => {
  const positions = usePinPositionsStore((state) => state.positions);

  return (
    <>
      {pinThreads.map((thread) => {
        const position = positions.get(thread.id);

        if (position === undefined || !position.visible) {
          return null;
        }

        const { left, top } = positionPinHitTarget(position);

        return (
          <button
            key={thread.id}
            type="button"
            aria-pressed={thread.id === selectedThreadId}
            className="pointer-events-auto absolute cursor-pointer rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pin-ring focus-visible:ring-offset-1"
            style={{
              left: `${String(left)}px`,
              top: `${String(top)}px`,
              width: `${String(PIN_HIT_TARGET_PX)}px`,
              height: `${String(PIN_HIT_TARGET_PX)}px`,
            }}
            onMouseEnter={() => {
              onHover(thread.id);
            }}
            onMouseLeave={() => {
              onHover(null);
            }}
            onFocus={() => {
              onHover(thread.id);
            }}
            onBlur={() => {
              onHover(null);
            }}
            onClick={() => {
              onSelect(thread.id);
            }}
          >
            <span className="sr-only">
              Open {thread.createdByName}&rsquo;s comment thread
            </span>
          </button>
        );
      })}
    </>
  );
};
